import { forwardRef, HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../utils/cn';

export interface NavItem {
  href: string;
  label: string;
  icon?: ReactNode;
  active?: boolean;
  disabled?: boolean;
  badge?: string | number;
}

export interface NavigationProps extends HTMLAttributes<HTMLElement> {
  items: NavItem[];
  variant?: 'horizontal' | 'vertical' | 'pills' | 'tabs';
  size?: 'sm' | 'md' | 'lg';
}

const Navigation = forwardRef<HTMLElement, NavigationProps>(
  ({ className, items, variant = 'horizontal', size = 'md', ...props }, ref) => {
    const getNavClasses = () => {
      const baseClasses = 'flex';
      
      const variantClasses = {
        horizontal: 'flex-row space-x-1',
        vertical: 'flex-col space-y-1',
        pills: 'flex-row space-x-2',
        tabs: 'flex-row border-b border-gray-200',
      };

      return cn(baseClasses, variantClasses[variant], className);
    };

    const getItemClasses = (item: NavItem) => {
      const baseClasses = 'inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50';
      
      const sizeClasses = {
        sm: 'h-8 px-3 text-xs',
        md: 'h-9 px-4 py-2',
        lg: 'h-10 px-6 text-base',
      };

      const variantClasses = {
        horizontal: item.active 
          ? 'bg-gray-100 text-gray-900' 
          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
        vertical: item.active 
          ? 'bg-gray-100 text-gray-900' 
          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
        pills: item.active 
          ? 'bg-blue-600 text-white' 
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
        tabs: item.active 
          ? 'border-b-2 border-blue-600 text-blue-600' 
          : 'text-gray-600 hover:text-gray-900 border-b-2 border-transparent',
      };

      const disabledClasses = item.disabled ? 'opacity-50 cursor-not-allowed' : '';

      return cn(
        baseClasses,
        sizeClasses[size],
        variantClasses[variant],
        disabledClasses
      );
    };

    return (
      <nav ref={ref} className={getNavClasses()} {...props}>
        {items?.map((item, index) => (
          <a
            key={index}
            href={item.disabled ? undefined : item.href}
            className={getItemClasses(item)}
            aria-current={item.active ? 'page' : undefined}
            onClick={item.disabled ? (e) => e.preventDefault() : undefined}
          >
            {item.icon && (
              <span className="mr-2 h-4 w-4">{item.icon}</span>
            )}
            {item.label}
            {item.badge && (
              <span className="ml-2 inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1.5 text-xs font-medium bg-gray-500 text-white rounded-full">
                {item.badge}
              </span>
            )}
          </a>
        ))}
      </nav>
    );
  }
);

Navigation.displayName = 'Navigation';

// Breadcrumb component
export interface BreadcrumbItem {
  href?: string;
  label: string;
  current?: boolean;
}

export interface BreadcrumbProps extends HTMLAttributes<HTMLElement> {
  items: BreadcrumbItem[];
  separator?: ReactNode;
}

const Breadcrumb = forwardRef<HTMLElement, BreadcrumbProps>(
  ({ className, items, separator = '/', ...props }, ref) => {
    return (
      <nav
        ref={ref}
        className={cn('flex', className)}
        aria-label="Breadcrumb"
        {...props}
      >
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          {items.map((item, index) => (
            <li key={index} className="inline-flex items-center">
              {index > 0 && (
                <span className="mx-2 text-gray-400">{separator}</span>
              )}
              {item.current ? (
                <span className="text-gray-500 font-medium">
                  {item.label}
                </span>
              ) : (
                <a
                  href={item.href}
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  {item.label}
                </a>
              )}
            </li>
          ))}
        </ol>
      </nav>
    );
  }
);

Breadcrumb.displayName = 'Breadcrumb';

export { Navigation, Breadcrumb };
